/**
 * Boots the cookie banner and consent-gated Google Analytics in the SPA.
 *
 * Razor pages get both from the server-rendered layout (CookieConsentHelper.GetPageEmbedScript +
 * MasterPageHelper). index.html is static, so the server only publishes `window.invSpaConsent` in
 * the initial-state script (SpaLayoutHelper.GetConsentJs) and this loads the same files the layout
 * does, in the same order: the banner library, its wrapper (cookieConsentWrapper) and the standalone
 * analyticsHelper bundle that exposes `window.inviton.consentGate`.
 *
 * consentGate then decides what may run, exactly as on the Razor pages: gtag.js is fetched only
 * once the visitor grants analytics, with the Consent Mode v2 defaults pushed before it.
 *
 * No config (white-label instance) = nothing is loaded.
 */
interface SpaConsentConfig {
	Styles: string[];
	Scripts: string[];
	BannerArgs: any;
	ScriptBinding: any;
}

export class ConsentLoader {
	private static started = false;

	static start(): void {
		const win = window as any;
		const config: SpaConsentConfig = win.invSpaConsent;
		if (config == null || ConsentLoader.started) {
			return;
		}

		ConsentLoader.started = true;
		config.Styles.forEach((href) => {
			const link = document.createElement('link');
			link.rel = 'stylesheet';
			link.href = href;
			document.head.appendChild(link);
		});

		//Same rule GetPageEmbedScript inlines on the Razor pages
		const style = document.createElement('style');
		style.textContent = '#s-bns #s-all-bn {float: right;margin-left: 5px;}';
		document.head.appendChild(style);

		ConsentLoader.loadScripts(config.Scripts, 0, () => {
			//The wrapper and consentGate.hasBanner() both expect the library instance on this global
			win.cookieConsent = win.initCookieConsent();
			win.cookieConsentWrapper.init(config.BannerArgs);

			win.invScriptBinding = config.ScriptBinding;
			win.inviton.consentGate.bootstrap();
		});
	}

	/** Sequential on purpose - each script depends on the globals of the previous one. */
	private static loadScripts(urls: string[], index: number, done: () => void): void {
		if (index >= urls.length) {
			done();
			return;
		}

		const script = document.createElement('script');
		script.src = urls[index];
		script.onload = () => ConsentLoader.loadScripts(urls, index + 1, done);

		//A failed file leaves the banner and GA off - never tracking without the banner in place
		script.onerror = () => console.warn('Consent loader: failed to load ' + urls[index]);
		document.head.appendChild(script);
	}
}
